import type { VisualPromptAnswers } from '@/types/visualPrompts/visualPrompt.types';

const SAVED_CHOICES_KEY = 'litium-visual-prompt-saved-choices';

const PERSISTED_ANSWER_KEYS = new Set([
  'task',
  'audience',
  'context',
  'tone',
  'constraints',
  'output',
  'goal',
  'matterRelated',
  'known',
  'unknown',
]);

const stripEphemeralAnswers = (
  answers: VisualPromptAnswers,
): VisualPromptAnswers => {
  const saved: VisualPromptAnswers = {};
  Object.entries(answers).forEach(([key, value]) => {
    if (PERSISTED_ANSWER_KEYS.has(key)) {
      saved[key] = value;
    }
  });
  return saved;
};

export const loadSavedChoicesForPrompt = (
  promptId: string,
): VisualPromptAnswers | null => {
  try {
    const raw = localStorage.getItem(SAVED_CHOICES_KEY);
    if (!raw) {
      return null;
    }
    const all = JSON.parse(raw) as Record<string, VisualPromptAnswers>;
    return all[promptId] ?? null;
  } catch {
    return null;
  }
};

export const saveChoicesForPrompt = (
  promptId: string,
  answers: VisualPromptAnswers,
): void => {
  try {
    const raw = localStorage.getItem(SAVED_CHOICES_KEY);
    const all: Record<string, VisualPromptAnswers> = raw
      ? (JSON.parse(raw) as Record<string, VisualPromptAnswers>)
      : {};
    all[promptId] = stripEphemeralAnswers(answers);
    localStorage.setItem(SAVED_CHOICES_KEY, JSON.stringify(all));
  } catch {
    // private mode / quota
  }
};

export const mergeSavedChoicesWithDefaults = (
  promptId: string,
  defaults?: VisualPromptAnswers,
): VisualPromptAnswers => {
  const saved = loadSavedChoicesForPrompt(promptId);
  return { ...defaults, ...saved };
};

export const clearSavedChoicesForPrompt = (promptId: string): void => {
  try {
    const raw = localStorage.getItem(SAVED_CHOICES_KEY);
    if (!raw) {
      return;
    }
    const all = JSON.parse(raw) as Record<string, VisualPromptAnswers>;
    if (!(promptId in all)) {
      return;
    }
    delete all[promptId];
    localStorage.setItem(SAVED_CHOICES_KEY, JSON.stringify(all));
  } catch {
    // private mode / quota
  }
};
